import { z } from 'zod';
import { assetId, certificateId, checkId, claimId, counterpartyId, entityId, evidenceId, jobId, orgId, recordId, userId, } from '../ids.js';
import { assetClass, checkLayer, checkNumber, checkState, corporateRegister, counterpartyRole, counterpartyStatus, decision, depth, entityStatus, evidenceKind, flagType, homeRegion, identifierType, jobStatus, jurisdiction, recordState, shareTier, sourceChannel, uboAccess, } from '../enums.js';
/** Garnet PRD §5.2–§5.4, §6, §11. */
export const Counterparty = z.object({
    id: counterpartyId,
    org_id: orgId,
    display_name: z.string().min(1),
    role_in_deal: counterpartyRole,
    primary_entity_id: entityId.nullable(),
    status: counterpartyStatus,
    created_at: z.string().datetime(),
});
export const Entity = z.object({
    id: entityId,
    jurisdiction,
    register: corporateRegister,
    register_id: z.string(),
    legal_name: z.string(),
    status: entityStatus,
    incorporation_date: z.string().datetime().nullable(),
    addresses: z.array(z.object({ kind: z.string(), lines: z.array(z.string()) })),
    /** Sensitive data class: PSC/UBO control records (PRD §17.1). */
    control: z.unknown().nullable(),
    ubo_access: uboAccess,
    evidence_ids: z.array(evidenceId),
});
export const Asset = z.object({
    id: assetId,
    class: assetClass,
    manufacturer_entity_id: entityId.nullable(),
    model: z.string(),
    identifier_type: identifierType,
    identifier_value: z.string(),
    risk_class: z.string().nullable(),
    nomenclature: z
        .object({ emdn: z.string().nullable(), gmdn: z.string().nullable() })
        .partial()
        .nullable(),
    intended_purpose: z.string().nullable(),
    certificate_refs: z.array(certificateId),
    jurisdictions: z.array(jurisdiction),
});
export const consignment = z.object({
    quantity: z.number().int().positive(),
    serials: z.array(z.string()).default([]),
    lots: z.array(z.string()).default([]),
    udi_pi: z.array(z.string()).default([]),
    provenance: z.enum(['new', 'refurbished', 'used']).nullable(),
    custody_chain: z
        .array(z.object({ holder: z.string(), from: z.string().nullable(), to: z.string().nullable() }))
        .default([]),
    customs_refs: z.array(z.string()).default([]),
    invoice_refs: z.array(z.string()).default([]),
});
export const Claim = z.object({
    id: claimId,
    org_id: orgId,
    counterparty_id: counterpartyId,
    asset_id: assetId.nullable(),
    consignment: consignment.nullable(),
    source_channel: sourceChannel,
    submitted_by: z.string(),
    submitted_at: z.string().datetime(),
    normalised: z.unknown().nullable(),
});
/** Field-level compare of claim against source — stored so a flag names its fields (§6.4 c1). */
export const matchResult = z.object({
    fields: z.array(z.object({
        field: z.string(),
        claimed: z.string().nullable(),
        resolved: z.string().nullable(),
        equal: z.boolean(),
    })),
});
export const flagDetail = z.object({
    type: flagType,
    /** Unit-scoped flags name the affected serials and never block the rest (§6.4 c6). */
    scope: z.enum(['record', 'unit']),
    affected_units: z.array(z.string()).default([]),
    detail: z.string(),
});
export const Check = z.object({
    id: checkId,
    org_id: orgId,
    job_id: jobId,
    number: checkNumber,
    layer: checkLayer,
    depth: depth.nullable(),
    state: checkState,
    source: z.string().nullable(),
    match: matchResult.nullable(),
    flags: z.array(flagDetail).default([]),
    evidence_ids: z.array(evidenceId),
    rule_version: z.string(),
    started_at: z.string().datetime().nullable(),
    completed_at: z.string().datetime().nullable(),
});
/** Immutable (Invariant I4): the provenance package is the only write path. */
export const Evidence = z.object({
    id: evidenceId,
    org_id: orgId,
    check_id: checkId.nullable(),
    kind: evidenceKind,
    source: z.string(),
    /** The issued query, redacted of secrets before persistence. */
    request: z.unknown(),
    /** Object-store pointer to the raw payload (WORM bucket). */
    payload_ref: z.string(),
    /** SHA-256 of the payload; chained per (org, chain) stream. */
    hash: z.string().regex(/^[0-9a-f]{64}$/),
    prev_hash: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
    chain: z.string(),
    seq: z.number().int().nonnegative(),
    captured_at: z.string().datetime(),
    residency_region: homeRegion,
});
export const Job = z.object({
    id: jobId,
    org_id: orgId,
    claim_id: claimId,
    /** Ordered check graph with chosen connectors (PRD §6.1). */
    plan: z.object({
        checks: z.array(z.object({
            number: checkNumber,
            depends_on: z.array(checkNumber),
            connector_ladder: z.array(z.string()),
        })),
    }),
    status: jobStatus,
    rule_version: z.string(),
    core_release: z.string().nullable(),
    started_at: z.string().datetime().nullable(),
    completed_at: z.string().datetime().nullable(),
});
export const weakestLink = z.object({
    check: checkNumber,
    state: checkState,
    depth: depth.nullable(),
    reason: z.string(),
});
export const decisionEvent = z.object({
    decision,
    rationale: z.string().min(1),
    conditions: z.array(z.string()).default([]),
    signed_by: userId,
    /** Set when a blocking flag was overridden — requires decision.override_blocking. */
    compliance_override_by: userId.nullable(),
    at: z.string().datetime(),
});
export const Record = z.object({
    id: recordId,
    org_id: orgId,
    counterparty_id: counterpartyId,
    version: z.number().int().positive(),
    supersedes: recordId.nullable(),
    checks: z.array(checkId),
    depth_reached: z.record(z.string(), depth.nullable()),
    weakest_link: weakestLink,
    state: recordState,
    decision: decisionEvent.nullable(),
    /** Versions that produced this record (Invariant I4). */
    versions: z.object({
        rule_engine: z.string(),
        core_release: z.string().nullable(),
        connector_mappings: z.record(z.string(), z.string()).default({}),
        model: z.string().nullable(),
    }),
    issued_at: z.string().datetime(),
    /** Detached Ed25519 signature over the canonical record form, with the signing key id. */
    signature: z.object({ key_id: z.string(), sig: z.string() }),
    share_tier: shareTier,
});
//# sourceMappingURL=verification.js.map